import { Component, ChangeDetectionStrategy, inject, signal, effect, computed } from "@angular/core";
import { ReactiveFormsModule, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { InputTextModule } from "primeng/inputtext";
import { TextareaModule } from "primeng/textarea";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { FloatLabelModule } from "primeng/floatlabel";

import { ChipModule } from "primeng/chip";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { FormsModule } from "@angular/forms";

import { RevertToSentQuote, QuoteStatus } from "src/api-service";
import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { strictEmailValidator } from "src/app/shared/validators/validations";
import { selectQuoteState } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { QuoteActions } from "src/app/store/pages/quote/quote.actions";
import { selectUserContext } from "src/app/store/user/user.selectors";

export type RevertQuoteDialogStage = "Select" | "Confirmation" | "Error";

@Component({
    selector: "app-revert-quote-dialog",
    templateUrl: "./revert-quote-dialog.component.html",
    styleUrls: ["./revert-quote-dialog.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        TranslateModule,
        CoreErrorsComponent,
        ButtonModule,
        CheckboxModule,
        ToggleSwitchModule,
        InputTextModule,
        TextareaModule,
        InputGroupModule,
        InputGroupAddonModule,
        FloatLabelModule,
        ChipModule,
    ],
})
export class RevertQuoteDialogComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    public readonly ref = inject(DynamicDialogRef);
    private readonly translateService = inject(TranslateService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly store = inject(Store);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly quote = toSignal(this.store.select(selectQuoteState));
    private readonly selectedVersion = toSignal(this.store.select(selectVersion));
    private readonly user = toSignal(this.store.select(selectUserContext));

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    public readonly currentStage = signal<RevertQuoteDialogStage>("Select");
    public readonly sendMailEnabled = signal(false);
    public form!: FormGroup;
    private formInitialized = false;
    public validCcEmails: string[] = [];
    public ccEmailControl = new FormControl("", strictEmailValidator());

    // =========================================================================
    // COMPUTED
    // =========================================================================
    public readonly companyId = computed(() =>
        this.user()?.selectedCompany?.id ?? ""
    );

    // =========================================================================
    // EFFECTS
    // =========================================================================
    private readonly initFormEffect = effect(() => {
        const quote = this.quote();
        const user = this.user();

        if (quote && user && !this.formInitialized) {
            this.formInitialized = true;
            this.initForm();
        }
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public closeDialog(): void {
        this.ref.close();
    }

    public onSendMailChange(value: boolean): void {
        this.sendMailEnabled.set(value);
        this.form.controls["sendMail"].setValue(value);

        if (value) {
            this.form.get("recipientEmail")?.setValidators([Validators.required, strictEmailValidator()]);
            this.form.get("message")?.setValidators(Validators.required);
        } else {
            this.form.get("recipientEmail")?.clearValidators();
            this.form.get("message")?.clearValidators();
        }
        this.form.get("recipientEmail")?.updateValueAndValidity();
        this.form.get("message")?.updateValueAndValidity();
    }

    public addCcRecipient(): void {
        const email = this.ccEmailControl.value?.trim() ?? "";

        if (!email) {
            return;
        }

        // Mark as touched to show validation errors
        this.ccEmailControl.markAsTouched();

        // Validate email
        if (this.ccEmailControl.invalid) {
            return;
        }

        // Check for duplicates
        if (this.validCcEmails.includes(email)) {
            this.ccEmailControl.reset();
            return;
        }

        // Add valid email
        this.validCcEmails.push(email);
        this.ccEmailControl.reset();
    }

    public removeCcRecipient(email: string): void {
        this.validCcEmails = this.validCcEmails.filter(e => e !== email);
    }

    public allowRowBreak(event: KeyboardEvent): void {
        if (event.key === "Enter") {
            event.stopPropagation();
        }
    }

    public overrideSubmit(event: KeyboardEvent): void {
        if (event.key === "Enter") {
            event.stopPropagation();
        }
    }

    public confirm(): void {
        if (this.form.invalid) return;

        const quote = this.quote();
        const version = this.selectedVersion();
        const user = this.user();

        if (!quote || !version || !user) return;

        const request: RevertToSentQuote = {
            sendMail: this.form.controls["sendMail"].value,
            subject: this.translateService.instant("REVERT_QUOTE.MAIL_SUBJECT", {
                quoteId: quote.id,
                companyId: this.companyId(),
            }),
            versionId: version.id,
            recipientEmail: this.form.controls["recipientEmail"].value,
            ccEmails: this.validCcEmails,
            message: this.buildEmailBody(
                this.form.controls["message"].value,
                this.form.controls["comment"].value,
                user.email,
            ),
            comment: this.form.controls["comment"].value,
        };

        this.store.dispatch(
            QuoteActions.updateBackToSent({
                quoteId: quote.id,
                request,
                dialogKey: 'BACK_TO_SENT',
            }),
        );
        this.ref.close();
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    private initForm(): void {
        const quote = this.quote();
        if (quote?.status === QuoteStatus.ReadyForReview) {
            this.initFormReadyForReview();
        } else {
            this.initFormUnderReview();
        }
    }

    private initFormReadyForReview(): void {
        const quote = this.quote();
        this.form = this.formBuilder.group({
            recipientEmail: [""],
            ccEmails: this.formBuilder.array([]),
            sendMail: [false],
            message: [
                {
                    value: this.translateService.instant("REVERT_QUOTE.MAIL_MESSAGE", {
                        quoteId: quote?.id,
                    }),
                    disabled: true,
                },
            ],
            showCommentField: [{ value: true, disabled: true }],
            comment: ["", Validators.required],
        });
    }

    private initFormUnderReview(): void {
        const quote = this.quote();
        const user = this.user();

        this.sendMailEnabled.set(true);

        this.form = this.formBuilder.group({
            recipientEmail: ["", [Validators.required, strictEmailValidator()]],
            ccEmails: this.formBuilder.array([]),
            sendMail: [true],
            message: [
                {
                    value: this.translateService.instant("REVERT_QUOTE.MAIL_MESSAGE", {
                        quoteId: quote?.id,
                    }),
                    disabled: true,
                },
                Validators.required,
            ],
            showCommentField: [{ value: true, disabled: true }],
            comment: ["", Validators.required],
        });

        this.setRecipientEmail(quote, user);
    }

    private setRecipientEmail(quote: any, user: any): void {
        let recipientEmail = quote?.salesInfo?.responsibleSalesPerson?.email ?? "";
        if (user?.email === quote?.salesInfo?.responsibleSalesPerson?.email) {
            recipientEmail = quote?.salesInfo?.salesSupportPerson?.email ?? "";
        }
        this.form.controls["recipientEmail"].setValue(recipientEmail);
    }

    private buildEmailBody(message: string, comment: string, email: string): string {
        let body = message ?? "";
        if (comment?.length > 0) {
            body += "\n" + this.translateService.instant("COMMENT") + ": " + comment;
        }
        body += "\n\n" + this.translateService.instant("MAIL.SENT_BY") + ": " + email;
        body += "\n" + this.translateService.instant("MAIL.NO_REPLY_INFO");
        return body;
    }
}
